import { generatePath } from 'react-router-dom'
import { NOTES_LIST_PER_PAGE } from '@common/constants/filters'
import { UPDATE_OPS_REPORTING } from '@common/constants/webSockets'
import { FileProcessingStatus } from '@common/interfaces/file'
import { Pages, WorkflowTypes } from '@common/interfaces/notes'
import { IOPSReporting } from '@common/interfaces/prospects'
import { IResponse } from '@common/interfaces/request'
import { Action } from 'redux'
import { select, takeEvery, put, call } from 'redux-saga/effects'
import { ROUTES } from '../../constants/routes'
import { putPromise } from '../../helpers/redux'
import { calculateOPSFlags } from '../flag/actions'
import { prospectWriteup } from '../generalAnalysis/actions'
import { sagaNavigate } from '../navigationHelper'
import { listNotes } from '../notes/actions'
import { putNotification } from '../ui/actions'
import {
  CREATE_SUCCESS,
  CREATE_PROSPECT_TERMS_SUCCESS,
  GENERATE_TERM_SHEET_SUCCESS,
  GENERATE_TERM_SHEET_FAILURE,
  listProspectTerms,
  refreshCount,
  START_REPORTING_SUCCESS,
  CREATE_NEW_OPS_SUCCESS,
  DELETE_PROSPECT_SUCCESS,
  SUBMIT_OPS_SUCCESS,
  showTermStructureSummaryInfo,
  show,
} from './actions'
import { reporting } from './selectors'

export function* createSuccess() {
  yield takeEvery(
    [CREATE_SUCCESS, CREATE_NEW_OPS_SUCCESS],
    function* (data: IResponse<{ id: string }> & Action) {
      yield call(sagaNavigate, generatePath(ROUTES.PROSPECT_PAGE, { id: data.data.id }))
    },
  )
}

export function* deleteProspectSuccess() {
  yield takeEvery(DELETE_PROSPECT_SUCCESS, function* () {
    yield call(sagaNavigate, ROUTES.PROSPECTS)
  })
}

export function* createProspectTermSuccess() {
  yield takeEvery(
    [CREATE_PROSPECT_TERMS_SUCCESS],
    function* (data: IResponse<{ termId: string }> & Action) {
      const { id }: { id: string } = yield select(reporting)
      yield call(
        sagaNavigate,
        generatePath(ROUTES.PROSPECT_EDIT_TERMS_PAGE, {
          id,
          termId: data.data.termId,
        }),
      )
    },
  )
}

export function* generateTermSheetSuccess() {
  yield takeEvery(
    GENERATE_TERM_SHEET_SUCCESS,
    function* (data: IResponse<{ boxLink: string }> & Action) {
      try {
        if (!data.data.boxLink) {
          yield put({
            type: GENERATE_TERM_SHEET_FAILURE,
            error: 'Unable to generate term sheet ',
          })
        }
        window.open(data.data.boxLink, '_blank')
      } catch (error: unknown) {
        yield put({ type: GENERATE_TERM_SHEET_FAILURE, error })
      }
    },
  )
}

export function* updateOPSReporting() {
  yield takeEvery(UPDATE_OPS_REPORTING, function* (action: IResponse & Action) {
    const data = action.data as {
      id?: string
      result?: { type?: string; status?: FileProcessingStatus | boolean }
    }
    const opsReporting: IOPSReporting = yield select(reporting)

    // Always increment refreshCount for flow status changes to trigger list refresh
    // For file updates, only refresh if viewing that specific prospect
    const isFlowStatusUpdate = data.result?.type === 'flow'
    const isViewingProspect = opsReporting?.id === data?.id

    if (isFlowStatusUpdate || isViewingProspect) {
      yield put(refreshCount())

      // If file processing started or completed, refresh the reporting data to show updated status
      if (data.result?.type === 'file' && isViewingProspect) {
        const status = data.result?.status
        if (
          status === FileProcessingStatus.PROCESSING ||
          status === FileProcessingStatus.COMPLETED
        ) {
          yield putPromise(show(opsReporting.id))
        }
      }

      // Only show notification if viewing the specific prospect
      if (isViewingProspect) {
        yield put(
          putNotification({
            code: '',
            message: `${opsReporting.clientName} OPS reporting ${
              data.result.status
                ? 'has been successfully updated.'
                : 'failed to update. Please try again.'
            }`,
            type: data.result.status ? 'success' : 'error',
          }),
        )
      }
    }
  })
}

export function* refreshOpsFlags() {
  yield takeEvery([START_REPORTING_SUCCESS], function* (action: IResponse & Action) {
    const data = action.data as { id?: string }
    if (data.id) {
      yield putPromise(calculateOPSFlags({ id: data.id }))
    }
  })
}

export function* submitOPSSuccess() {
  yield takeEvery([SUBMIT_OPS_SUCCESS], function* () {
    const { id } = (yield select(reporting)) as { id: string }

    yield putPromise(showTermStructureSummaryInfo(id))
    // The service flips the submitted term row's status server-side, and the
    // terms table now sits directly under the button, so refetch the list it
    // reads — nothing else remounts it.
    yield putPromise(listProspectTerms(id))
    // Refresh the Deal Summary write-up so its status pill flips to In Review
    // and the "Submit OPS" button hides now that it's submitted.
    yield putPromise(prospectWriteup({ opsReportingId: id }))
    yield putPromise(
      listNotes(id, {
        filters: {
          pages: [Pages.ops],
        },
        orderBy: 'created_at',
        orderDirection: 'DESC',
        workflow: WorkflowTypes.opsFlow,
        page: 0,
        perPage: NOTES_LIST_PER_PAGE,
        skipLoader: true,
      }),
    )
  })
}
